import { useId } from 'react'
import { type IconDef, type IconName, PATHS, strokeFor } from './icons/paths.ts'

/**
 * Temaly's own icon set: "the desk and the sheet" at 24px. Rules (docs/DESIGN.md section 6):
 *
 * - 24x24 grid, live area 2..22. One stroke: 1.75 at 24px, held at 1.5px on screen below that
 *   (`strokeFor`), round caps and joins, `currentColor`.
 * - Corners: radius 2 on the grid (the 8/12/16 family at icon scale), 1.25-1.5 on small parts.
 * - Optical sizes: circles r 9 (18 across), squares 16 across, so both read the same size.
 * - The family: the sheet with a folded top right corner. Every tool mark, copy, print and the
 *   logo are built on it.
 * - The marker: tool marks carry one small accent shape filled with `var(--icon-accent,
 *   currentColor)`. ToolMark sets it to amber; anywhere else it stays one colour.
 * - Weights: `regular` outline (default); `fill` = active (the silhouette tinted, for selected
 *   tabs and nav); `bold` = a heavier stroke for small verdict marks.
 *
 * 20px in UI (`size-5`, the default), 24px in tiles (`size-6`). Decorative by default
 * (aria-hidden); pass `title` when the icon is the only thing carrying meaning.
 */
export type IconProps = {
	className?: string
	/** Pixel size; otherwise read from a `size-*` class (default 20). */
	size?: number
	title?: string
	weight?: 'regular' | 'fill' | 'bold'
}

const pxFrom = (className?: string) => {
	const m = className?.match(/(?:^|\s)size-(\d+(?:\.\d+)?|\[(\d+(?:\.\d+)?)(px|rem)\])(?:\s|$)/)
	if (!m) return 20
	if (m[2]) return Number(m[2]) * (m[3] === 'rem' ? 16 : 1)
	return Number(m[1]) * 4
}

/** The shared base: draws one icon definition. */
export function Svg({
	def,
	className,
	size,
	title,
	weight = 'regular',
}: IconProps & { def: IconDef }) {
	const px = size ?? pxFrom(className)
	const stroke = strokeFor(px) + (weight === 'bold' ? 0.5 : 0)
	const uid = useId().replace(/:/g, '')
	const solid = weight === 'fill' && !!def.f
	return (
		<svg
			viewBox="0 0 24 24"
			width={size}
			height={size}
			className={className ?? (size ? undefined : 'size-5')}
			fill="none"
			stroke="currentColor"
			strokeWidth={stroke}
			strokeLinecap="round"
			strokeLinejoin="round"
			role={title ? 'img' : undefined}
			aria-hidden={title ? undefined : true}
			focusable="false"
		>
			{title && <title>{title}</title>}
			{solid ? (
				<>
					{/*
					 * Active = a solid body with its inner lines cut out of it. The cut is the stroke
					 * path kept only where it lies inside the silhouette shrunk by a stroke (erode),
					 * so the outline stays whole and only the details show through.
					 */}
					<defs>
						<filter id={`${uid}e`} x="-10%" y="-10%" width="120%" height="120%">
							<feMorphology operator="erode" radius={stroke * 0.9} />
						</filter>
						<mask id={`${uid}i`} maskUnits="userSpaceOnUse" x="0" y="0" width="24" height="24">
							<path d={def.f} fill="#fff" stroke="none" filter={`url(#${uid}e)`} />
						</mask>
						<mask id={`${uid}k`} maskUnits="userSpaceOnUse" x="0" y="0" width="24" height="24">
							<rect width="24" height="24" fill="#fff" />
							{def.s && <path d={def.s} stroke="#000" mask={`url(#${uid}i)`} />}
							{def.d && <path d={def.d} fill="#000" stroke="none" mask={`url(#${uid}i)`} />}
						</mask>
					</defs>
					<g mask={`url(#${uid}k)`}>
						<path d={def.f} fill="currentColor" stroke="none" />
						{def.s && <path d={def.s} />}
					</g>
				</>
			) : (
				def.s && <path d={def.s} />
			)}
			{/* At UI sizes (24px and under, often drawn smaller by CSS) the marker grows by a stroke of its own colour, or it reads as a speck. */}
			{def.a && (
				<path
					d={def.a}
					fill="var(--icon-accent, currentColor)"
					stroke={px <= 24 ? 'var(--icon-accent, currentColor)' : 'none'}
					strokeWidth={1.25}
				/>
			)}
			{def.d && !solid && <path d={def.d} fill="currentColor" stroke="none" />}
		</svg>
	)
}

const icon = (name: IconName) => {
	const def: IconDef = PATHS[name]
	return (props: IconProps) => <Svg def={def} {...props} />
}

// Direction
export const IconForward = /*#__PURE__*/ icon('arrowRight')
export const IconBack = /*#__PURE__*/ icon('arrowLeft')
export const IconUp = /*#__PURE__*/ icon('arrowUp')
export const IconDown = /*#__PURE__*/ icon('arrowDown')
export const IconCaret = /*#__PURE__*/ icon('chevronDown')
export const IconChevronUp = /*#__PURE__*/ icon('chevronUp')
export const IconNext = /*#__PURE__*/ icon('chevronRight')
export const IconChevronLeft = /*#__PURE__*/ icon('chevronLeft')
export const IconExternal = /*#__PURE__*/ icon('external')
export const IconRefresh = /*#__PURE__*/ icon('refresh')
/** Back ten seconds. */
export const IconBack10 = /*#__PURE__*/ icon('back10')
export const IconUndo = /*#__PURE__*/ icon('undo')
export const IconFlip = /*#__PURE__*/ icon('flip')
export const IconSwap = /*#__PURE__*/ icon('swap')
export const IconShuffle = /*#__PURE__*/ icon('shuffle')

// Actions
export const IconPlus = /*#__PURE__*/ icon('plus')
export const IconMinus = /*#__PURE__*/ icon('minus')
export const IconCheck = /*#__PURE__*/ icon('check')
export const IconClose = /*#__PURE__*/ icon('close')
export const IconTrash = /*#__PURE__*/ icon('trash')
export const IconPencil = /*#__PURE__*/ icon('pencil')
export const IconCopy = /*#__PURE__*/ icon('copy')
export const IconLink = /*#__PURE__*/ icon('link')
export const IconBrokenLink = /*#__PURE__*/ icon('brokenLink')
export const IconShare = /*#__PURE__*/ icon('share')
export const IconPrinter = /*#__PURE__*/ icon('printer')
export const IconUpload = /*#__PURE__*/ icon('upload')
/** Keep your work: save to an account. */
export const IconCloudUp = /*#__PURE__*/ icon('cloudUp')
export const IconSearch = /*#__PURE__*/ icon('search')
export const IconSignOut = /*#__PURE__*/ icon('signOut')
export const IconMenu = /*#__PURE__*/ icon('menu')
export const IconMore = /*#__PURE__*/ icon('more')
export const IconGrip = /*#__PURE__*/ icon('grip')
export const IconSettings = /*#__PURE__*/ icon('settings')
export const IconFullscreen = /*#__PURE__*/ icon('fullscreen')
export const IconFullscreenExit = /*#__PURE__*/ icon('fullscreenExit')
export const IconEye = /*#__PURE__*/ icon('eye')
export const IconEyeOff = /*#__PURE__*/ icon('eyeOff')
export const IconLock = /*#__PURE__*/ icon('lock')

// Media and sources
export const IconPlay = /*#__PURE__*/ icon('play')
export const IconPause = /*#__PURE__*/ icon('pause')
export const IconWatch = /*#__PURE__*/ icon('watch')
export const IconVolume = /*#__PURE__*/ icon('volume')
export const IconVolumeOff = /*#__PURE__*/ icon('volumeOff')
export const IconVideo = /*#__PURE__*/ icon('video')
export const IconCamera = /*#__PURE__*/ icon('camera')
export const IconPhoto = /*#__PURE__*/ icon('photo')
export const IconText = /*#__PURE__*/ icon('text')

// Status
export const IconCheckCircle = /*#__PURE__*/ icon('checkCircle')
export const IconError = /*#__PURE__*/ icon('error')
export const IconAlert = /*#__PURE__*/ icon('alert')
export const IconInfo = /*#__PURE__*/ icon('info')
export const IconHelp = /*#__PURE__*/ icon('help')
export const IconWarning = /*#__PURE__*/ icon('warning')
export const IconOffline = /*#__PURE__*/ icon('offline')
export const IconClock = /*#__PURE__*/ icon('clock')
export const IconCalendar = /*#__PURE__*/ icon('calendar')

// People and places
export const IconUser = /*#__PURE__*/ icon('user')
export const IconUsers = /*#__PURE__*/ icon('users')
export const IconGroups = /*#__PURE__*/ icon('groups')
export const IconGlobe = /*#__PURE__*/ icon('globe')
export const IconPhone = /*#__PURE__*/ icon('phone')

// The desk
export const IconSheet = /*#__PURE__*/ icon('sheet')
export const IconFolder = /*#__PURE__*/ icon('folder')
export const IconTools = /*#__PURE__*/ icon('tools')
export const IconList = /*#__PURE__*/ icon('list')
export const IconChart = /*#__PURE__*/ icon('chart')
export const IconStar = /*#__PURE__*/ icon('star')
/** AI drafting: a pen nib with a glint. */
export const IconSparkles = /*#__PURE__*/ icon('sparkle')
export const IconCrossword = /*#__PURE__*/ icon('crossword')
export const IconWordSearch = /*#__PURE__*/ icon('wordSearch')
export const IconMatch = /*#__PURE__*/ icon('match')
export const IconGap = /*#__PURE__*/ icon('gap')

/** Tool marks (carry the marker accent). */
export const IconQuestionSheet = /*#__PURE__*/ icon('questionSheet')
export const IconCards = /*#__PURE__*/ icon('cards')
export const IconLevels = /*#__PURE__*/ icon('levels')
export const IconLessonPlan = /*#__PURE__*/ icon('lessonPlan')
export const IconWheel = /*#__PURE__*/ icon('wheel')
